<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import { isAxiosError } from 'axios'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type {
  Event,
  ExternalGradeImportError,
  ExternalGradeImportFileDTO,
  GradeImportBatchResult,
  GradeRecordDTO,
  SetGradeRequest,
  Slot,
} from '@/types'

import AttendanceGradeExternalLinkCard from '@/components/events/AttendanceGradeExternalLinkCard.vue'
import FinalGradeExportLinkCard from '@/components/events/FinalGradeExportLinkCard.vue'
import { toAppColor } from '@/composables/useAppColor'
import { appConfirm } from '@/composables/useAppConfirm'
import { appTableSortableHeader } from '@/composables/useAppTable'
import { useAppToast } from '@/composables/useAppToast'
import { useDelayedLoading } from '@/composables/useDelayedLoading'
import {
  downloadGradeImportFile,
  exportCampusGradesXlsx,
  getEventGrades,
  getGradeImportFiles,
  importGradesXlsx,
  setEventGrades,
} from '@/services/attendanceGradeService'
import { readBlobError } from '@/services/downloadBlob'
import { slotService } from '@/services/slotService'
import { VALID_GRADES } from '@/types'

const props = defineProps<{
  event: Event
}>()

defineEmits<{
  (e: 'reload-event'): void
}>()

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t, d, n } = useI18n()
const toast = useAppToast()

const loading = ref(false)
const showLoading = useDelayedLoading(loading)
const saving = ref(false)
const grades = ref<GradeRecordDTO[]>([])
const gradeColumns = computed<TableColumn<GradeRecordDTO>[]>(() => [
  {
    accessorKey: 'userName',
    header: appTableSortableHeader<GradeRecordDTO>(t('features.grades.participant')),
  },
  { id: 'grade', header: t('features.grades.grade') },
  { id: 'credits', header: t('features.grades.credits') },
  { accessorKey: 'finalGrade', header: t('features.grades.savedGrade') },
  {
    accessorKey: 'gradedAt',
    header: appTableSortableHeader<GradeRecordDTO>(t('features.grades.lastChange')),
  },
])
const hasChanges = ref(false)

// Grade edits live in a local map until "Save", and this page registered no navigation guard,
// so leaving it dropped them silently.
const { setHasUnsavedChanges } = useUnsavedChangesProtection({
  confirmMessage: t('features.grades.unsavedLeaveMessage'),
  confirmHeader: t('features.grades.unsavedLeaveHeader'),
})

watch(hasChanges, (dirty) => setHasUnsavedChanges(dirty), { immediate: true })
const importFiles = ref<ExternalGradeImportFileDTO[]>([])
const importFilesLoading = ref(false)
const downloadingImportIds = ref<Set<string>>(new Set())
const xlsxImportDialogVisible = ref(false)
/** select → review the verified batch → result of the applied import. */
const xlsxImportStep = ref<'select' | 'review' | 'result'>('select')
const xlsxImportFiles = ref<File[]>([])
const xlsxVerifying = ref(false)
const xlsxImporting = ref(false)
const xlsxImportReview = ref<GradeImportBatchResult | null>(null)
const xlsxImportResult = ref<GradeImportBatchResult | null>(null)
/**
 * Positions of the files the organizer keeps. Positions, not names, because two course leaders
 * routinely hand in a file of the same name.
 */
const xlsxSelectedIndices = ref<Set<number>>(new Set())
const xlsxImportError = ref<string | null>(null)
const xlsxUpload = useTemplateRef('xlsxUpload')
const canManageImports = computed(
  () => props.event.permissions?.manageRegistrationStatuses === true,
)
const canExportGrades = computed(() => props.event.permissions?.exportParticipants === true)
const campusExporting = ref(false)

// Slot scope selection
const slots = ref<Slot[]>([])
const selectedScope = ref<number>(0)
const appliedScope = ref<number>(0)

interface ScopeOption {
  label: string
  value: number
}

const scopeOptions = computed<ScopeOption[]>(() => {
  const options: ScopeOption[] = [{ label: t('features.grades.scopeEvent'), value: 0 }]
  for (const slot of slots.value) {
    options.push({ label: slot.name ?? `Slot #${slot.id}`, value: slot.id })
  }
  return options
})

const hasSlots = computed(() => slots.value.length > 0)

const appliedScopeLabel = computed(
  () =>
    scopeOptions.value.find((option) => option.value === appliedScope.value)?.label ??
    t('features.grades.scopeEvent'),
)

/**
 * Grade and credits change together: a row is pending when either differs from what is stored,
 * and both are sent when it is saved. Keeping them in one entry is what lets the "unsaved" badge,
 * the changed-only filter and the pending counter stay a single question.
 */
interface PendingGrade {
  finalGrade: string | null
  finalGradeCredits: number | null
}

// Track pending changes: key=`${registrationId}-${appUserId}` -> pending grade + credits
const pendingGrades = ref<Map<string, PendingGrade>>(new Map())

function getKey(registrationId: number, appUserId: string): string {
  return `${registrationId}::${appUserId}`
}

function formatGrade(grade: string): string {
  if (grade === 'pass') return t('enums.grade.pass')
  if (grade === 'fail') return t('enums.grade.fail')
  return grade
}

const gradeOptions = computed(() => [
  { label: '—', value: null as string | null },
  ...VALID_GRADES.map((g) => ({
    label: formatGrade(g),
    value: g as string | null,
  })),
])

function getCurrentGrade(grade: GradeRecordDTO): string | null {
  const key = getKey(grade.registrationId, grade.appUserId)
  const pending = pendingGrades.value.get(key)
  return pending ? pending.finalGrade : (grade.finalGrade ?? null)
}

function getCurrentCredits(grade: GradeRecordDTO): number | null {
  const key = getKey(grade.registrationId, grade.appUserId)
  const pending = pendingGrades.value.get(key)
  return pending ? pending.finalGradeCredits : (grade.finalGradeCredits ?? null)
}

function isPending(grade: GradeRecordDTO): boolean {
  return pendingGrades.value.has(getKey(grade.registrationId, grade.appUserId))
}

/** Drops the entry again when the edit brought the row back to its stored values. */
function applyPending(grade: GradeRecordDTO, next: PendingGrade) {
  const key = getKey(grade.registrationId, grade.appUserId)
  if (
    next.finalGrade === (grade.finalGrade ?? null) &&
    next.finalGradeCredits === (grade.finalGradeCredits ?? null)
  ) {
    pendingGrades.value.delete(key)
  } else {
    pendingGrades.value.set(key, next)
  }
  hasChanges.value = pendingGrades.value.size > 0
}

function setGrade(grade: GradeRecordDTO, value: string | null) {
  // Clearing the grade clears the credits it earned — mirror what the backend will do.
  applyPending(grade, {
    finalGrade: value,
    finalGradeCredits: value === null ? null : getCurrentCredits(grade),
  })
}

function setCredits(grade: GradeRecordDTO, value: number | null) {
  applyPending(grade, {
    finalGrade: getCurrentGrade(grade),
    finalGradeCredits: value ?? null,
  })
}

// Filtering — a course roster can be long, so the table gets its own search and status filter.
const searchQuery = ref('')
const statusFilter = ref<'all' | 'graded' | 'ungraded' | 'changed'>('all')

const statusFilterOptions = computed(() => [
  { label: t('features.grades.filters.all'), value: 'all' as const },
  { label: t('features.grades.filters.graded'), value: 'graded' as const },
  { label: t('features.grades.filters.ungraded'), value: 'ungraded' as const },
  { label: t('features.grades.filters.changed'), value: 'changed' as const },
])

const filteredGrades = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  return grades.value.filter((grade) => {
    if (query && !(grade.userName ?? '').toLowerCase().includes(query)) return false
    if (statusFilter.value === 'graded') return !!getCurrentGrade(grade)
    if (statusFilter.value === 'ungraded') return !getCurrentGrade(grade)
    if (statusFilter.value === 'changed') return isPending(grade)
    return true
  })
})

const stats = computed(() => {
  const total = grades.value.length
  const graded = grades.value.filter((grade) => !!getCurrentGrade(grade)).length
  return {
    total,
    graded,
    ungraded: total - graded,
    pending: pendingGrades.value.size,
    percentage: total > 0 ? Math.round((graded / total) * 100) : 0,
  }
})

function resetFilters() {
  searchQuery.value = ''
  statusFilter.value = 'all'
}

async function loadGrades() {
  loading.value = true
  try {
    const slotId = appliedScope.value === 0 ? undefined : appliedScope.value
    const data = await getEventGrades(props.event.id, slotId)
    grades.value = data.grades
    pendingGrades.value.clear()
    hasChanges.value = false
  } catch (err) {
    console.error('Failed to load grades:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.grades.loadError'),
      duration: 5000,
    })
  } finally {
    loading.value = false
  }
}

async function saveChanges() {
  if (pendingGrades.value.size === 0) return
  saving.value = true
  try {
    const gradeEntries: SetGradeRequest[] = Array.from(pendingGrades.value.entries()).map(
      ([key, pending]) => {
        const separatorIndex = key.indexOf('::')
        const registrationId = key.substring(0, separatorIndex)
        const appUserId = key.substring(separatorIndex + 2)
        return {
          registrationId: parseInt(registrationId),
          appUserId,
          finalGrade: pending.finalGrade,
          finalGradeCredits: pending.finalGradeCredits,
        }
      },
    )
    await setEventGrades(props.event.id, { grades: gradeEntries })
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('features.grades.saveSuccess'),
      duration: 3000,
    })
    await loadGrades()
  } catch (err) {
    console.error('Failed to save grades:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.grades.saveError'),
      duration: 5000,
    })
  } finally {
    saving.value = false
  }
}

async function loadImportFiles() {
  if (!canManageImports.value) {
    importFiles.value = []
    return
  }

  importFilesLoading.value = true
  try {
    importFiles.value = await getGradeImportFiles(props.event.id)
  } catch (err) {
    console.error('Failed to load grade import files:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.grades.importHistory.loadError'),
      duration: 5000,
    })
  } finally {
    importFilesLoading.value = false
  }
}

async function downloadImportFile(fileId: string) {
  downloadingImportIds.value.add(fileId)
  try {
    await downloadGradeImportFile(props.event.id, fileId)
  } catch (err) {
    console.error('Failed to download grade import file:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.grades.importHistory.downloadError'),
      duration: 5000,
    })
  } finally {
    downloadingImportIds.value.delete(fileId)
  }
}

function openXlsxImportDialog() {
  resetXlsxImport()
  xlsxImportDialogVisible.value = true
}

/**
 * Downloads the saved grades of the current scope in the campus management import format.
 * Unsaved edits are not part of the file, so they are confirmed away before the download.
 */
async function exportCampusGrades() {
  if (hasChanges.value) {
    const accepted = await appConfirm({
      message: t('features.grades.campusExport.unsavedMessage'),
      title: t('features.grades.campusExport.unsavedHeader'),
      cancelLabel: t('common.actions.cancel'),
      confirmLabel: t('features.grades.campusExport.exportAnyway'),
    })
    if (!accepted) return
  }

  campusExporting.value = true
  try {
    await exportCampusGradesXlsx(
      props.event.id,
      appliedScope.value === 0 ? undefined : appliedScope.value,
    )
  } catch (err) {
    console.error('Campus grade export failed:', err)
    const parsed = await readBlobError(err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description:
        parsed?.code === 'noGradedParticipants'
          ? t('features.grades.campusExport.noGradedParticipants')
          : t('features.grades.campusExport.error'),
      duration: 6000,
    })
  } finally {
    campusExporting.value = false
  }
}

function onXlsxImportFileSelect(event: { files: File[] }) {
  // The picker accumulates across repeated selections, so its list is the staged batch.
  xlsxImportFiles.value = event.files
  backToXlsxSelection()
}

function removeXlsxImportFile(index: number) {
  const remaining = xlsxImportFiles.value.filter((_, position) => position !== index)
  xlsxImportFiles.value = remaining
  xlsxUpload.value?.setFiles(remaining)
  backToXlsxSelection()
}

function backToXlsxSelection() {
  xlsxImportStep.value = 'select'
  xlsxImportReview.value = null
  xlsxImportResult.value = null
  xlsxSelectedIndices.value = new Set()
  xlsxImportError.value = null
}

function resetXlsxImport() {
  xlsxImportFiles.value = []
  backToXlsxSelection()
}

function toggleXlsxFileSelection(index: number, selected: boolean) {
  const next = new Set(xlsxSelectedIndices.value)
  if (selected) next.add(index)
  else next.delete(index)
  xlsxSelectedIndices.value = next
}

const xlsxReviewFiles = computed(() => xlsxImportReview.value?.files ?? [])

const xlsxSelectedCount = computed(() => xlsxSelectedIndices.value.size)

const xlsxSelectedTotals = computed(() =>
  xlsxReviewFiles.value.reduce(
    (totals, file, index) => {
      if (!xlsxSelectedIndices.value.has(index)) return totals
      return {
        updated: totals.updated + file.updated,
        unchanged: totals.unchanged + file.unchanged,
        blankSkipped: totals.blankSkipped + file.blankSkipped,
      }
    },
    { updated: 0, unchanged: 0, blankSkipped: 0 },
  ),
)

function getXlsxImportRowError(error: ExternalGradeImportError): string {
  // Quote the rejected cell back when the backend told us what it was — otherwise the row
  // only says the grade was unsupported, without saying which one.
  if (error.code === 'invalidGrade' && error.value) {
    return t('features.external.xlsxImport.errors.invalidGradeValue', { value: error.value })
  }

  if (error.code === 'invalidCredits' && error.value) {
    return t('features.external.xlsxImport.errors.invalidCreditsValue', { value: error.value })
  }

  if (error.code === 'duplicateAcrossFiles') {
    return t('features.grades.importUpload.errors.duplicateAcrossFiles', { file: error.value })
  }

  const knownCodes = new Set([
    'missingIdentifier',
    'duplicateIdentifier',
    'invalidGrade',
    'invalidCredits',
    'noMatch',
    'ambiguousMatch',
    'duplicateParticipant',
  ])
  return knownCodes.has(error.code)
    ? t(`features.external.xlsxImport.errors.${error.code}`)
    : error.message
}

function getXlsxImportFileError(code: string): string {
  const knownCodes = new Set(['emptyFile', 'fileTooLarge', 'unsupportedFile', 'invalidWorkbook'])
  return knownCodes.has(code)
    ? t(`features.external.xlsxImport.requestErrors.${code}`)
    : t('features.external.xlsxImport.importError')
}

function getXlsxImportRequestError(code: string | undefined, fallback: string | undefined): string {
  const batchCodes = new Set(['noFiles', 'tooManyFiles', 'batchTooLarge'])
  if (code && batchCodes.has(code)) {
    return t(`features.grades.importUpload.requestErrors.${code}`)
  }

  const knownCodes = new Set([
    'emptyFile',
    'fileTooLarge',
    'unsupportedFile',
    'invalidWorkbook',
    'invalidRows',
  ])
  return code && knownCodes.has(code)
    ? t(`features.external.xlsxImport.requestErrors.${code}`)
    : (fallback ?? t('features.external.xlsxImport.importError'))
}

/** Dry run over every staged file. Nothing is written; the batch is only reported back. */
async function verifyXlsxImport() {
  if (xlsxImportFiles.value.length === 0) {
    xlsxImportError.value = t('features.external.xlsxImport.noFile')
    return
  }

  xlsxVerifying.value = true
  xlsxImportError.value = null

  try {
    const review = await importGradesXlsx(
      props.event.id,
      xlsxImportFiles.value,
      appliedScope.value === 0 ? undefined : appliedScope.value,
      true,
    )
    xlsxImportReview.value = review
    // Everything that can be imported is pre-selected; a file with errors cannot be picked.
    xlsxSelectedIndices.value = new Set(
      review.files.flatMap((file, index) => (file.valid ? [index] : [])),
    )
    xlsxImportStep.value = 'review'
  } catch (err) {
    handleXlsxImportError(err)
  } finally {
    xlsxVerifying.value = false
  }
}

async function importGrades() {
  if (xlsxSelectedCount.value === 0) {
    xlsxImportError.value = t('features.grades.importUpload.noSelection')
    return
  }

  if (!hasChanges.value) {
    await performXlsxImport()
    return
  }

  const accepted = await appConfirm({
    message: t('features.external.xlsxImport.unsavedMessage'),
    title: t('features.external.xlsxImport.unsavedHeader'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.discard'),
    confirmColor: 'error',
  })
  if (accepted) await performXlsxImport()
}

async function performXlsxImport() {
  const selectedFiles = xlsxImportFiles.value.filter((_, index) =>
    xlsxSelectedIndices.value.has(index),
  )
  if (selectedFiles.length === 0) return

  xlsxImporting.value = true
  xlsxImportError.value = null

  try {
    const result = await importGradesXlsx(
      props.event.id,
      selectedFiles,
      appliedScope.value === 0 ? undefined : appliedScope.value,
    )
    xlsxImportResult.value = result

    if (!result.applied) {
      // A file that verified cleanly can still fail here if the grades changed in between.
      // Send the organizer back to the review with the fresh findings.
      xlsxImportReview.value = result
      xlsxImportFiles.value = selectedFiles
      xlsxImportResult.value = null
      xlsxSelectedIndices.value = new Set(
        result.files.flatMap((file, index) => (file.valid ? [index] : [])),
      )
      xlsxImportStep.value = 'review'
      xlsxImportError.value = t('features.grades.importUpload.staleVerification')
      return
    }

    xlsxImportStep.value = 'result'
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('features.external.xlsxImport.success', { updated: result.updated }),
      duration: 4000,
    })
    await Promise.all([loadGrades(), loadImportFiles()])
  } catch (err) {
    handleXlsxImportError(err)
  } finally {
    xlsxImporting.value = false
  }
}

function handleXlsxImportError(err: unknown) {
  console.error('Failed to import grades:', err)
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { detail?: string; error?: string; code?: string }
      | undefined
    xlsxImportError.value = getXlsxImportRequestError(data?.code, data?.detail ?? data?.error)
  } else {
    xlsxImportError.value = t('features.external.xlsxImport.importError')
  }
}

function getGradeSeverity(grade: string | null): string {
  if (!grade) return 'secondary'
  if (grade === 'fail' || grade === '5.0') return 'danger'
  if (grade === 'pass') return 'success'
  const num = parseFloat(grade)
  if (num <= 2.0) return 'success'
  if (num <= 3.0) return 'info'
  return 'warn'
}

async function loadSlots() {
  try {
    slots.value = await slotService.getAll(props.event.id)
  } catch {
    slots.value = []
  }
}

onMounted(() => {
  appliedScope.value = selectedScope.value
  loadSlots()
  loadGrades()
  loadImportFiles()
})

watch(
  () => props.event.id,
  () => {
    appliedScope.value = 0
    selectedScope.value = 0
    resetFilters()
    loadSlots()
    loadGrades()
    loadImportFiles()
  },
)

watch(selectedScope, async (newScope) => {
  if (newScope === appliedScope.value) {
    return
  }

  const applyScopeChange = () => {
    appliedScope.value = newScope
    loadGrades()
  }

  if (!hasChanges.value) {
    applyScopeChange()
    return
  }

  const accepted = await appConfirm({
    message: t('features.grades.scopeChangeConfirmMessage'),
    title: t('features.grades.scopeChangeConfirmHeader'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.discard'),
    confirmColor: 'error',
  })
  if (accepted) applyScopeChange()
  else selectedScope.value = appliedScope.value
})
</script>

<template>
  <div class="flex flex-col gap-4" :class="{ 'pb-20': hasChanges }">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 class="m-0 text-2xl font-semibold">
          {{ t('features.grades.title') }}
        </h2>
        <p class="mt-1 mb-0 text-muted">
          {{ t('features.grades.subtitle') }}
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <UButton
          v-if="canExportGrades"
          :label="t('features.grades.campusExport.button')"
          icon="i-lucide-file-spreadsheet"
          color="neutral"
          variant="outline"
          :loading="campusExporting"
          @click="exportCampusGrades"
        />
        <UButton
          v-if="canManageImports"
          :label="t('features.external.xlsxImport.openDialog')"
          icon="i-lucide-upload"
          color="neutral"
          variant="outline"
          @click="openXlsxImportDialog"
        />
      </div>
    </div>

    <!-- Grading progress at a glance -->
    <div v-if="!loading && grades.length > 0" class="mycard flex flex-col gap-4 p-4">
      <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div class="flex flex-col">
          <span class="text-sm text-muted">
            {{ t('features.grades.stats.participants') }}
          </span>
          <span class="text-2xl font-semibold">{{ stats.total }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm text-muted">
            {{ t('features.grades.stats.graded') }}
          </span>
          <span class="text-2xl font-semibold text-success">{{ stats.graded }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm text-muted">
            {{ t('features.grades.stats.ungraded') }}
          </span>
          <span class="text-2xl font-semibold">{{ stats.ungraded }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm text-muted">
            {{ t('features.grades.stats.pending') }}
          </span>
          <span
            class="text-2xl font-semibold"
            :class="stats.pending > 0 ? 'text-warning' : undefined"
          >
            {{ stats.pending }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <UProgress :model-value="stats.percentage" class="h-2 flex-1" />
        <span class="shrink-0 text-sm text-muted">
          {{ t('features.grades.stats.progress', { percentage: stats.percentage }) }}
        </span>
      </div>
    </div>

    <!-- Scope, search and status filter -->
    <div class="mycard flex flex-col gap-3 p-4 lg:flex-row lg:items-end">
      <div v-if="hasSlots" class="w-full lg:w-72">
        <label for="grades-scope" class="mb-1 block text-sm font-medium">
          {{ t('features.grades.scopeLabel') }}
        </label>
        <AppSelect
          id="grades-scope"
          v-model="selectedScope"
          :options="scopeOptions"
          option-label="label"
          option-value="value"
          class="w-full"
        />
      </div>
      <div class="flex-1">
        <label for="grades-search" class="mb-1 block text-sm font-medium">
          {{ t('common.actions.search') }}
        </label>
        <AppIconField>
          <AppInputIcon name="i-lucide-search" />
          <UInput
            id="grades-search"
            v-model="searchQuery"
            :placeholder="t('features.grades.filters.searchPlaceholder')"
            class="w-full"
          />
        </AppIconField>
      </div>
      <div class="w-full lg:w-56">
        <label for="grades-status" class="mb-1 block text-sm font-medium">
          {{ t('features.grades.filters.status') }}
        </label>
        <AppSelect
          id="grades-status"
          v-model="statusFilter"
          :options="statusFilterOptions"
          option-label="label"
          option-value="value"
          class="w-full"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="showLoading" class="py-8 text-center">
      <AppSpinner style="width: 3rem; height: 3rem" />
    </div>

    <!-- No participants -->
    <div v-else-if="grades.length === 0" class="mycard p-8 text-center">
      <UIcon name="i-lucide-users" class="mb-2 text-4xl text-dimmed" />
      <p class="text-muted">
        {{ t('features.grades.noParticipants') }}
      </p>
    </div>

    <!-- Filtered to nothing -->
    <div v-else-if="filteredGrades.length === 0" class="mycard p-8 text-center">
      <UIcon name="i-lucide-search-x" class="mb-2 text-4xl text-dimmed" />
      <p class="mb-3 text-muted">
        {{ t('features.grades.noResults') }}
      </p>
      <UButton
        :label="t('features.grades.filters.reset')"
        color="neutral"
        variant="outline"
        @click="resetFilters"
      />
    </div>

    <!-- Grades table -->
    <div v-else class="mycard p-0">
      <AppDataTableShell
        :data="filteredGrades"
        :columns="gradeColumns"
        paginate
        :page-size="25"
        sticky="header"
      >
        <template #userName-cell="{ row }">
          <div class="flex items-center gap-2">
            <span class="font-medium">{{
              row.original.userName ?? t('common.terms.unknown')
            }}</span>
            <UBadge
              v-if="isPending(row.original)"
              :label="t('features.grades.unsaved')"
              color="warning"
              variant="subtle"
              size="sm"
            />
          </div>
        </template>
        <template #grade-cell="{ row }">
          <AppSelect
            :model-value="getCurrentGrade(row.original)"
            :options="gradeOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('features.grades.selectGrade')"
            :aria-label="t('features.grades.grade')"
            class="w-36"
            @update:model-value="(val: string | null) => setGrade(row.original, val)"
          />
        </template>
        <template #credits-cell="{ row }">
          <UInputNumber
            :model-value="getCurrentCredits(row.original)"
            :min="0"
            :max="100"
            :step="0.5"
            :disabled="!getCurrentGrade(row.original)"
            :placeholder="t('features.grades.creditsDefault')"
            :aria-label="t('features.grades.credits')"
            class="w-32"
            @update:model-value="(val: number | null) => setCredits(row.original, val)"
          />
        </template>
        <template #finalGrade-cell="{ row }">
          <div v-if="row.original.finalGrade" class="flex items-center gap-2">
            <UBadge
              :label="formatGrade(row.original.finalGrade)"
              :color="toAppColor(getGradeSeverity(row.original.finalGrade))"
              variant="subtle"
            />
            <span class="text-xs text-muted">
              {{
                row.original.finalGradeCredits != null
                  ? t('features.grades.creditsValue', { value: n(row.original.finalGradeCredits) })
                  : t('features.grades.creditsDefault')
              }}
            </span>
          </div>
          <span v-else class="text-dimmed">—</span>
        </template>
        <template #gradedAt-cell="{ row }">
          <div v-if="row.original.gradedAt" class="flex flex-col text-sm">
            <span class="text-neutral-700 dark:text-neutral-300">
              {{ d(new Date(row.original.gradedAt), 'datetime') }}
            </span>
            <span v-if="row.original.gradedByUserName" class="text-xs text-muted">
              {{ t('features.grades.gradedByLine', { name: row.original.gradedByUserName }) }}
            </span>
          </div>
          <span v-else class="text-dimmed">—</span>
        </template>
      </AppDataTableShell>
    </div>

    <!-- Secondary tools: sharing links and upload history stay out of the way until needed -->
    <div
      v-if="canManageImports || props.event.permissions?.manageFinalGradeExport"
      class="mycard overflow-hidden"
    >
      <div class="flex items-center gap-3 p-4">
        <span
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-primary-100 text-[var(--app-text-primary)] dark:border-primary-400/40 dark:bg-primary-400/20"
        >
          <UIcon name="i-lucide-share-2" class="text-lg leading-none" />
        </span>
        <h3 class="m-0 min-w-0 flex-1 font-semibold">{{ t('features.grades.tools.title') }}</h3>
        <UBadge
          v-if="importFiles.length > 0"
          :label="t('features.grades.tools.fileCount', importFiles.length)"
          color="neutral"
          variant="subtle"
        />
      </div>
      <div
        class="divide-y divide-neutral-200 border-t border-neutral-200 dark:divide-neutral-700 dark:border-neutral-700"
      >
        <div class="p-4">
          <AttendanceGradeExternalLinkCard :event-id="props.event.id" :scope="appliedScope" bare />
        </div>
        <div
          v-if="appliedScope === 0 && props.event.permissions?.manageFinalGradeExport"
          class="p-4"
        >
          <FinalGradeExportLinkCard :event-id="props.event.id" bare />
        </div>

        <section v-if="canManageImports" class="p-4">
          <div class="mb-2 flex items-center gap-2">
            <UIcon name="i-lucide-file-spreadsheet" class="text-muted" />
            <span class="font-semibold">{{ t('features.grades.importHistory.title') }}</span>
            <UButton
              :label="t('features.external.xlsxImport.openDialog')"
              icon="i-lucide-upload"
              color="neutral"
              size="sm"
              variant="outline"
              class="ml-auto"
              @click="openXlsxImportDialog"
            />
          </div>
          <p class="mt-0 mb-3 text-sm text-muted">
            {{ t('features.grades.importHistory.description') }}
          </p>

          <div v-if="importFilesLoading" class="py-4 text-center">
            <AppSpinner style="width: 2rem; height: 2rem" />
          </div>
          <p
            v-else-if="importFiles.length === 0"
            class="m-0 rounded-lg bg-neutral-50 p-3 text-sm text-muted dark:bg-neutral-800 dark:text-neutral-400"
          >
            {{ t('features.grades.importHistory.empty') }}
          </p>
          <ul v-else class="m-0 space-y-2 p-0">
            <li
              v-for="importedFile in importFiles"
              :key="importedFile.id"
              class="flex flex-wrap items-center gap-3 rounded-lg border border-neutral-200 p-3 dark:border-neutral-700"
            >
              <div class="min-w-0 flex-1">
                <div class="truncate font-medium">{{ importedFile.fileName }}</div>
                <div class="mt-1 flex flex-wrap items-center gap-2">
                  <span class="text-xs text-muted">
                    {{ d(new Date(importedFile.uploadedAt), 'datetime') }}
                  </span>
                  <UBadge
                    :label="
                      importedFile.slotId
                        ? (importedFile.slotName ??
                          t('features.grades.importHistory.slotFallback', {
                            slotId: importedFile.slotId,
                          }))
                        : t('features.grades.importHistory.eventScope')
                    "
                    color="neutral"
                  />
                </div>
              </div>
              <UButton
                :label="t('features.grades.importHistory.download')"
                icon="i-lucide-download"
                color="neutral"
                size="sm"
                :loading="downloadingImportIds.has(importedFile.id)"
                @click="downloadImportFile(importedFile.id)"
                variant="outline"
              />
            </li>
          </ul>
        </section>
      </div>
    </div>

    <!-- Unsaved changes stay reachable no matter how far the roster is scrolled -->
    <div
      v-if="hasChanges"
      class="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-center px-4"
    >
      <div
        class="pointer-events-auto flex items-center gap-3 rounded-full border border-neutral-200 bg-white px-4 py-2 shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
      >
        <span class="text-sm font-medium">
          {{ t('features.grades.unsavedChanges', stats.pending) }}
        </span>
        <UButton
          :label="t('common.actions.reset')"
          color="neutral"
          size="sm"
          variant="ghost"
          @click="loadGrades"
        />
        <UButton
          :label="t('common.actions.save')"
          size="sm"
          :loading="saving"
          @click="saveChanges"
        />
      </div>
    </div>

    <AppModal
      v-model:open="xlsxImportDialogVisible"
      :title="t('features.external.xlsxImport.dialogTitle')"
      modal
      :style="{ width: '44rem', maxWidth: 'calc(100vw - 2rem)' }"
    >
      <form class="flex flex-col gap-5" @submit.prevent="importGrades">
        <p class="text-sm text-muted">
          {{ t('features.grades.importUpload.dialogSubtitle') }}
        </p>

        <p v-if="hasSlots" class="m-0 text-sm text-muted">
          {{ t('features.grades.importUpload.scopeHint', { scope: appliedScopeLabel }) }}
        </p>

        <!-- Step 1: stage the workbooks -->
        <div v-if="xlsxImportStep === 'select'" class="flex flex-col gap-2">
          <span id="manage-xlsx-file-label" class="font-medium">
            {{ t('features.external.xlsxImport.fileLabel') }}
          </span>
          <div
            class="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-neutral-300 p-6 dark:border-neutral-600"
          >
            <UIcon
              name="i-lucide-file-spreadsheet"
              class="text-4xl text-green-700 dark:text-green-400"
              aria-hidden="true"
            />
            <AppFileUpload
              ref="xlsxUpload"
              mode="basic"
              accept=".xlsx"
              multiple
              :max-file-size="5242880"
              :auto="false"
              :choose-label="t('features.grades.importUpload.chooseFiles')"
              :choose-button-props="{
                'aria-labelledby': 'manage-xlsx-file-label',
                'aria-describedby': 'manage-xlsx-file-help',
              }"
              @select="onXlsxImportFileSelect"
            />
          </div>

          <ul v-if="xlsxImportFiles.length" class="flex flex-col gap-2">
            <li
              v-for="(file, index) in xlsxImportFiles"
              :key="`${file.name}-${file.size}-${index}`"
              class="flex items-center gap-3 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800"
            >
              <UIcon
                name="i-lucide-file-spreadsheet"
                class="text-2xl text-green-700 dark:text-green-400"
                aria-hidden="true"
              />
              <div class="min-w-0 flex-1">
                <div class="truncate font-medium">{{ file.name }}</div>
                <div class="text-xs text-muted">{{ (file.size / 1024).toFixed(1) }} KB</div>
              </div>
              <UButton
                type="button"
                icon="i-lucide-x"
                color="error"
                :aria-label="`${t('common.actions.remove')}: ${file.name}`"
                variant="ghost"
                @click="removeXlsxImportFile(index)"
              />
            </li>
          </ul>

          <small id="manage-xlsx-file-help" class="text-muted">
            {{ t('features.grades.importUpload.filesHelp') }}
          </small>
        </div>

        <!-- Step 2: the verified batch, one row per workbook -->
        <div v-else-if="xlsxImportStep === 'review'" class="flex flex-col gap-3">
          <p class="m-0 text-sm text-muted" aria-live="polite">
            {{
              t('features.grades.importUpload.reviewSummary', {
                selected: xlsxSelectedCount,
                total: xlsxReviewFiles.length,
                updated: xlsxSelectedTotals.updated,
                unchanged: xlsxSelectedTotals.unchanged,
              })
            }}
          </p>

          <ul class="flex max-h-96 flex-col gap-2 overflow-auto">
            <li
              v-for="(file, index) in xlsxReviewFiles"
              :key="`${file.fileName}-${index}`"
              class="rounded-lg border p-3"
              :class="
                file.valid
                  ? 'border-neutral-200 dark:border-neutral-700'
                  : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950'
              "
            >
              <div class="flex items-start gap-3">
                <UCheckbox
                  :model-value="xlsxSelectedIndices.has(index)"
                  :disabled="!file.valid"
                  :aria-label="file.fileName"
                  @update:model-value="toggleXlsxFileSelection(index, $event === true)"
                />
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="truncate font-medium">{{ file.fileName }}</span>
                    <UBadge
                      v-if="file.detectedSlotName"
                      color="neutral"
                      variant="subtle"
                      size="sm"
                      :label="file.detectedSlotName"
                    />
                    <UBadge
                      v-else-if="file.valid"
                      color="neutral"
                      variant="subtle"
                      size="sm"
                      :label="t('features.grades.scopeEvent')"
                    />
                  </div>
                  <p
                    v-if="file.fileErrorCode"
                    class="m-0 mt-1 text-sm text-red-700 dark:text-red-300"
                  >
                    {{ getXlsxImportFileError(file.fileErrorCode) }}
                  </p>
                  <p v-else class="m-0 mt-1 text-xs text-muted">
                    {{
                      t('features.grades.importUpload.fileCounts', {
                        examined: file.examined,
                        updated: file.updated,
                        unchanged: file.unchanged,
                        blankSkipped: file.blankSkipped,
                      })
                    }}
                  </p>

                  <ul v-if="file.errors.length" class="mt-2 space-y-2 text-sm">
                    <li
                      v-for="error in file.errors"
                      :key="`${error.worksheet}-${error.row}-${error.identifier ?? ''}`"
                      class="rounded-lg border border-red-200 bg-red-50 p-2 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
                    >
                      <strong
                        >{{ error.worksheet }}, {{ t('features.external.xlsxImport.row') }}
                        {{ error.row }}</strong
                      >
                      <span v-if="error.identifier"> · {{ error.identifier }}</span>
                      <div>{{ getXlsxImportRowError(error) }}</div>
                    </li>
                  </ul>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Step 3: what was written -->
        <div v-else class="flex flex-col gap-3">
          <AppAlert v-if="xlsxImportResult" color="success" :closable="false" aria-live="polite">
            {{
              t('features.external.xlsxImport.summary', {
                examined: xlsxImportResult.files.reduce((sum, file) => sum + file.examined, 0),
                updated: xlsxImportResult.updated,
                unchanged: xlsxImportResult.unchanged,
                blankSkipped: xlsxImportResult.blankSkipped,
              })
            }}
          </AppAlert>

          <ul v-if="xlsxImportResult" class="flex flex-col gap-2 text-sm">
            <li
              v-for="(file, index) in xlsxImportResult.files"
              :key="`${file.fileName}-${index}`"
              class="flex flex-wrap items-center gap-2 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800"
            >
              <span class="truncate font-medium">{{ file.fileName }}</span>
              <UBadge
                color="neutral"
                variant="subtle"
                size="sm"
                :label="file.detectedSlotName ?? t('features.grades.scopeEvent')"
              />
              <span class="text-muted">
                {{ t('features.grades.importUpload.fileUpdated', { updated: file.updated }) }}
              </span>
            </li>
          </ul>
        </div>

        <AppAlert v-if="xlsxImportError" color="error" :closable="false" role="alert">
          {{ xlsxImportError }}
        </AppAlert>

        <div class="flex justify-end gap-2">
          <UButton
            v-if="xlsxImportStep === 'review'"
            type="button"
            :label="t('common.labels.back')"
            color="neutral"
            variant="ghost"
            @click="backToXlsxSelection"
          />
          <UButton
            type="button"
            :label="
              xlsxImportStep === 'result' ? t('common.actions.close') : t('common.actions.cancel')
            "
            color="neutral"
            variant="ghost"
            @click="xlsxImportDialogVisible = false"
          />
          <UButton
            v-if="xlsxImportStep === 'select'"
            type="button"
            :label="t('features.grades.importUpload.verify')"
            icon="i-lucide-list-checks"
            :disabled="xlsxImportFiles.length === 0"
            :loading="xlsxVerifying"
            @click="verifyXlsxImport"
          />
          <UButton
            v-else-if="xlsxImportStep === 'review'"
            type="submit"
            :label="t('features.grades.importUpload.importSelected', { count: xlsxSelectedCount })"
            icon="i-lucide-upload"
            :disabled="xlsxSelectedCount === 0"
            :loading="xlsxImporting"
          />
        </div>
      </form>
    </AppModal>
  </div>
</template>
